import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { charleyMemoryConfig } from './config';
import { CmBackButton } from './CmBackButton';
import {
  fetchTodos,
  createTodo,
  toggleTodoDone,
  deleteTodo,
  loginWithPassword,
  trackEvent,
  updateTodo,
  reorderTodos,
} from './api';
import { EVENTS } from './events';
import { linkify } from './linkify';
import bgImage from './assets/bg.png';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Page = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  padding: 80px 20px 60px;
  box-sizing: border-box;
  background-image:
    linear-gradient(180deg, rgba(73, 79, 95, 0.72) 0%, rgba(7, 10, 18, 0.88) 100%),
    url(${bgImage});
  background-size: auto, cover;
  background-repeat: no-repeat;
  background-position: 0 0, 50% 100%;
  color: #e9ecf1;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  @media (max-width: 480px) {
    padding: 48px 12px 40px;
  }
`;

const ContentArea = styled.div`
  width: 100%;
  max-width: 560px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  animation: ${fadeIn} 0.4s ease;
`;

const Title = styled.h1`
  font-size: 1.6rem;
  margin: 0;
  color: #faf6e4;
  text-align: center;
  @media (max-width: 480px) { font-size: 1.3rem; }
`;

const Subtitle = styled.p`
  margin: 0;
  opacity: 0.65;
  font-size: 0.9rem;
  text-align: center;
`;

const LoginCard = styled.form`
  background: rgba(12, 15, 25, 0.88);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  max-width: 360px;
  margin: 24px auto 0;
`;

const Input = styled.input`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 10px 14px;
  color: #e9ecf1;
  font-size: 0.92rem;
  outline: none;
  transition: border-color 0.15s;
  &::placeholder { color: rgba(233, 236, 241, 0.35); }
  &:focus { border-color: rgba(250, 246, 228, 0.35); }
`;

const Btn = styled.button`
  border: none;
  border-radius: 999px;
  padding: 10px 28px;
  font-size: 0.95rem;
  cursor: pointer;
  background: #f8f1da;
  color: #24211c;
  font-weight: 600;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  transition: transform 0.12s ease, box-shadow 0.12s ease;
  &:hover { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(0, 0, 0, 0.35); }
  &:disabled { opacity: 0.5; cursor: default; transform: none; box-shadow: none; }
`;

const ErrorBox = styled.div`
  padding: 10px 14px;
  border-radius: 8px;
  background: rgba(255, 99, 132, 0.08);
  border: 1px solid rgba(255, 99, 132, 0.3);
  color: #ffd8df;
  font-size: 0.85rem;
`;

const AddRow = styled.form`
  display: flex;
  gap: 8px;
`;

const AddInput = styled(Input)`
  flex: 1;
`;

const SelectWrap = styled.div`
  position: relative;
  display: inline-block;
`;

const SelectBtn = styled.button`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 12px 14px;
  color: #e9ecf1;
  font-size: 1.1rem;
  line-height: 1.2;
  min-height: 48px;
  outline: none;
  transition: border-color 0.15s, background 0.15s;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 220px;
  user-select: none;
  &:focus { border-color: rgba(250, 246, 228, 0.35); }
  &:hover { background: rgba(255, 255, 255, 0.07); }
  @media (max-width: 480px) {
    font-size: 1.15rem;
    min-height: 52px;
  }
`;

const SelectChevron = styled.span`
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  opacity: 0.8;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const SelectMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  z-index: 2000;
  background: rgba(12, 15, 25, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.55);
  overflow: hidden;
`;

const SelectMenuScroll = styled.div`
  max-height: min(52vh, 360px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const SelectItem = styled.button`
  width: 100%;
  text-align: left;
  border: none;
  background: ${(p) => (p.$active ? 'rgba(255, 255, 255, 0.10)' : 'transparent')};
  color: ${(p) => (p.$selected ? '#faf6e4' : 'rgba(233, 236, 241, 0.92)')};
  padding: 14px 14px;
  font-size: 1.1rem;
  line-height: 1.25;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  &:hover { background: rgba(255, 255, 255, 0.10); }
  &:focus { outline: none; background: rgba(255, 255, 255, 0.12); }
  @media (max-width: 480px) {
    font-size: 1.15rem;
    padding: 16px 14px;
  }
`;

const CheckMark = styled.span`
  flex-shrink: 0;
  opacity: 0.9;
`;

const ToolbarRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
`;

const Segmented = styled.div`
  display: inline-flex;
  gap: 2px;
  padding: 2px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const SegBtn = styled.button`
  border: none;
  border-radius: 999px;
  padding: 8px 12px;
  font-size: 0.86rem;
  cursor: pointer;
  background: ${(p) => (p.$active ? '#f8f1da' : 'transparent')};
  color: ${(p) => (p.$active ? '#24211c' : 'rgba(233, 236, 241, 0.85)')};
  transition: transform 0.12s ease, opacity 0.12s ease;
  &:hover { transform: translateY(-1px); opacity: 0.95; }
`;

const TodoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const TodoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  opacity: ${(p) => (p.$done ? 0.5 : 1)};
  transition: opacity 0.2s;
`;

const Checkbox = styled.button`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 2px solid ${(p) => (p.$checked ? '#c8e6c0' : 'rgba(255,255,255,0.2)')};
  background: ${(p) => (p.$checked ? 'rgba(200, 230, 192, 0.15)' : 'transparent')};
  color: #c8e6c0;
  font-size: 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin-top: 1px;
  transition: border-color 0.15s, background 0.15s;
  &:hover { border-color: rgba(250, 246, 228, 0.5); }
`;

const TodoText = styled.span`
  flex: 1;
  font-size: 0.92rem;
  line-height: 1.5;
  text-decoration: ${(p) => (p.$done ? 'line-through' : 'none')};
  word-break: break-word;
  a {
    color: #a8d0ff;
    text-decoration: underline;
    text-underline-offset: 2px;
    word-break: break-all;
    &:hover { color: #cfe3ff; }
  }
`;

const TodoMeta = styled.span`
  font-size: 0.72rem;
  opacity: 0.4;
  white-space: nowrap;
  margin-top: 3px;
`;

const DragHandle = styled.span`
  flex-shrink: 0;
  width: 18px;
  margin-top: 2px;
  cursor: grab;
  opacity: 0.5;
  user-select: none;
  line-height: 1;
  touch-action: none;
`;

const SmallBtn = styled.button`
  border: none;
  background: none;
  color: rgba(250, 246, 228, 0.7);
  font-size: 0.78rem;
  cursor: pointer;
  padding: 2px 6px;
  margin-top: 2px;
  opacity: 0.75;
  transition: opacity 0.15s;
  &:hover { opacity: 1; }
`;

const DeleteBtn = styled.button`
  border: none;
  background: none;
  color: rgba(255, 99, 132, 0.5);
  font-size: 0.75rem;
  cursor: pointer;
  padding: 2px 4px;
  margin-top: 2px;
  transition: color 0.15s;
  &:hover { color: rgba(255, 99, 132, 0.9); }
`;

const Label = styled.label`
  font-size: 0.82rem;
  opacity: 0.6;
  margin-bottom: -8px;
`;

const InlineEditRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const InlineEditActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`;

const InlineInput = styled(Input)`
  width: 100%;
`;

const CategoryInput = styled(Input)`
  width: 100%;
`;

const InlineHint = styled.span`
  font-size: 0.78rem;
  opacity: 0.55;
`;

const parseTs = (ts) => {
  try {
    const t = new Date(ts).getTime();
    return Number.isFinite(t) ? t : 0;
  } catch {
    return 0;
  }
};

function BigCategorySelect({ value, onChange, options, ariaLabel }) {
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  const itemRefs = useRef([]);
  const [open, setOpen] = useState(false);

  const selectedIdx = useMemo(() => {
    const idx = options.findIndex((o) => o.value === value);
    return idx >= 0 ? idx : 0;
  }, [options, value]);

  const [activeIdx, setActiveIdx] = useState(selectedIdx);

  useEffect(() => {
    setActiveIdx(selectedIdx);
  }, [selectedIdx]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (!wrapRef.current) return;
      if (wrapRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const el = itemRefs.current[activeIdx];
    if (el) el.focus();
  }, [open, activeIdx]);

  const commit = useCallback((nextValue) => {
    onChange(nextValue);
    setOpen(false);
    btnRef.current?.focus();
  }, [onChange]);

  const onBtnKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setActiveIdx(selectedIdx);
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen((v) => !v);
    }
  }, [selectedIdx]);

  const onMenuKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      btnRef.current?.focus();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(options.length - 1, i + 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
      return;
    }
    if (e.key === 'Home') {
      e.preventDefault();
      setActiveIdx(0);
      return;
    }
    if (e.key === 'End') {
      e.preventDefault();
      setActiveIdx(options.length - 1);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const opt = options[activeIdx];
      if (opt) commit(opt.value);
    }
  }, [activeIdx, commit, options]);

  const selectedLabel = options[selectedIdx]?.label ?? '';

  return (
    <SelectWrap ref={wrapRef}>
      <SelectBtn
        ref={btnRef}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onBtnKeyDown}
      >
        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selectedLabel}
        </span>
        <SelectChevron aria-hidden>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </SelectChevron>
      </SelectBtn>

      {open && (
        <SelectMenu role="listbox" aria-label={ariaLabel} onKeyDown={onMenuKeyDown}>
          <SelectMenuScroll>
            {options.map((opt, idx) => (
              <SelectItem
                key={opt.value || '__empty'}
                ref={(el) => { itemRefs.current[idx] = el; }}
                type="button"
                role="option"
                aria-selected={opt.value === value}
                $selected={opt.value === value}
                $active={idx === activeIdx}
                onMouseEnter={() => setActiveIdx(idx)}
                onClick={() => commit(opt.value)}
              >
                <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {opt.label}
                </span>
                {opt.value === value && <CheckMark aria-hidden>✓</CheckMark>}
              </SelectItem>
            ))}
          </SelectMenuScroll>
        </SelectMenu>
      )}
    </SelectWrap>
  );
}

const SortableTodoItem = ({ id, disabled, children, style, ...rest }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const dndStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.75 : undefined,
    ...(style || {}),
  };

  // Inject handle props into the DragHandle child while keeping layout intact.
  const enhancedChildren = React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return child;
    const isHandle = child.type === DragHandle;
    if (!isHandle) return child;
    return React.cloneElement(child, {
      ref: setActivatorNodeRef,
      ...attributes,
      ...listeners,
    });
  });

  return (
    <TodoItem ref={setNodeRef} style={dndStyle} {...rest}>
      {enhancedChildren}
    </TodoItem>
  );
};

const CharleyMemoryTodosPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newText, setNewText] = useState('');
  const [adding, setAdding] = useState(false);

  const [categoryFilter, setCategoryFilter] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [pendingSortMode, setPendingSortMode] = useState('created_at'); // 'created_at' | 'manual'
  const [manualPendingIds, setManualPendingIds] = useState([]);

  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const apiBase = (charleyMemoryConfig.apiBaseUrl || '').replace(/\/$/, '');
  const navPassword = typeof location.state?.cm_password === 'string' ? location.state.cm_password : '';
  const usedNavPasswordRef = useRef(false);

  useEffect(() => {
    trackEvent(EVENTS.TODOS_PAGE_VIEW);
    if (!apiBase) return;
    fetchTodos('shared')
      .then((data) => { setTodos(Array.isArray(data) ? data : []); setAuthed(true); })
      .catch(() => {});
  }, [apiBase]);

  useEffect(() => {
    if (usedNavPasswordRef.current) return;
    if (!apiBase) return;
    const pwd = (navPassword || '').trim();
    if (!pwd) return;
    usedNavPasswordRef.current = true;

    // Clear navigation state ASAP so direct URL / refresh won't keep the password.
    navigate(location.pathname, { replace: true, state: {} });

    if (authed) return;
    setPassword(pwd);
    setAuthLoading(true);
    setAuthError('');
    (async () => {
      try {
        await loginWithPassword({ apiBaseUrl: apiBase, password: pwd });
        const data = await fetchTodos('shared');
        setTodos(Array.isArray(data) ? data : []);
        setAuthed(true);
        trackEvent(EVENTS.TODOS_AUTH_SUCCESS, { via: 'nav_state' });
      } catch {
        setAuthError('密碼不正確');
      } finally {
        setAuthLoading(false);
      }
    })();
  }, [apiBase, authed, location.pathname, navPassword, navigate]);

  const handleLogin = useCallback(async (e) => {
    e.preventDefault();
    if (!apiBase || !password.trim()) return;
    setAuthLoading(true);
    setAuthError('');
    try {
      await loginWithPassword({ apiBaseUrl: apiBase, password: password.trim() });
      const data = await fetchTodos('shared');
      setTodos(Array.isArray(data) ? data : []);
      setAuthed(true);
      trackEvent(EVENTS.TODOS_AUTH_SUCCESS);
    } catch {
      setAuthError('密碼不正確');
    } finally {
      setAuthLoading(false);
    }
  }, [apiBase, password]);

  const loadTodos = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchTodos('shared');
      setTodos(Array.isArray(data) ? data : []);
    } catch { /* */ }
    setLoading(false);
  }, []);

  const handleAdd = useCallback(async (e) => {
    e.preventDefault();
    const text = newText.trim();
    if (!text) return;
    setAdding(true);
    try {
      const created = await createTodo({ text, createdBy: '', type: 'shared', category: newCategory.trim() });
      setTodos((prev) => [created, ...prev]);
      setNewText('');
      setNewCategory('');
      if (pendingSortMode === 'manual' && !categoryFilter) {
        setManualPendingIds((prev) => {
          const next = [created.id, ...prev.filter((id) => id !== created.id)];
          reorderTodos({ type: 'shared', ids: next }).catch(() => {});
          return next;
        });
      }
    } catch { /* */ }
    setAdding(false);
  }, [categoryFilter, newCategory, newText, pendingSortMode]);

  const handleToggle = useCallback(async (id) => {
    const nowIso = new Date().toISOString();
    setTodos((prev) => prev.map((t) => {
      if (t.id !== id) return t;
      const nextDone = !t.is_done;
      return {
        ...t,
        is_done: nextDone,
        completed_at: nextDone ? nowIso : null,
        manual_order: nextDone ? null : t.manual_order,
      };
    }));
    if (pendingSortMode === 'manual') {
      setManualPendingIds((prev) => {
        const exists = prev.includes(id);
        // If it was pending -> done, remove. If done -> pending, append to end.
        const wasPending = exists;
        if (wasPending) return prev.filter((x) => x !== id);
        return [...prev, id];
      });
    }
    try { await toggleTodoDone(id); } catch { loadTodos(); }
  }, [loadTodos, pendingSortMode]);

  const handleDelete = useCallback(async (id) => {
    setTodos((prev) => prev.filter((t) => t.id !== id));
    setManualPendingIds((prev) => prev.filter((x) => x !== id));
    try { await deleteTodo(id); } catch { loadTodos(); }
  }, [loadTodos]);

  const beginEdit = useCallback((t) => {
    setEditingId(t.id);
    setEditText(t.text || '');
    setEditCategory(t.category || '');
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditText('');
    setEditCategory('');
    setSavingEdit(false);
  }, []);

  const saveEdit = useCallback(async () => {
    if (!editingId) return;
    const text = editText.trim();
    if (!text) return;
    setSavingEdit(true);
    try {
      const nextCategory = editCategory.trim();
      await updateTodo(editingId, { text, category: nextCategory });
      setTodos((prev) => {
        // If we are filtering a category and user changed it, remove from current list.
        if (categoryFilter && nextCategory !== categoryFilter) {
          return prev.filter((t) => t.id !== editingId);
        }
        return prev.map((t) => (t.id === editingId ? { ...t, text, category: nextCategory } : t));
      });
      setManualPendingIds((prev) => prev.filter((id) => id !== editingId));
      cancelEdit();
    } catch {
      setSavingEdit(false);
    }
  }, [cancelEdit, categoryFilter, editCategory, editText, editingId]);

  const allCategories = useMemo(() => {
    const set = new Set();
    todos.forEach((t) => {
      const c = (t.category || '').trim();
      if (c) set.add(c);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [todos]);

  const filteredTodos = useMemo(() => {
    if (!categoryFilter) return todos;
    return todos.filter((t) => (t.category || '').trim() === categoryFilter);
  }, [categoryFilter, todos]);

  const pending = useMemo(() => filteredTodos.filter((t) => !t.is_done), [filteredTodos]);
  const done = useMemo(() => filteredTodos.filter((t) => t.is_done), [filteredTodos]);

  const doneSorted = useMemo(() => {
    const list = [...done];
    list.sort((a, b) => {
      const ta = parseTs(a.completed_at || a.updated_at || a.created_at);
      const tb = parseTs(b.completed_at || b.updated_at || b.created_at);
      return tb - ta;
    });
    return list;
  }, [done]);

  const pendingCreatedSorted = useMemo(() => {
    const list = [...pending];
    list.sort((a, b) => parseTs(b.created_at) - parseTs(a.created_at));
    return list;
  }, [pending]);

  const pendingManualSorted = useMemo(() => {
    // Base ordering by manual_order (if exists), else created_at (newest first)
    const base = [...pending].sort((a, b) => {
      const ao = Number.isFinite(a.manual_order) ? a.manual_order : null;
      const bo = Number.isFinite(b.manual_order) ? b.manual_order : null;
      if (ao != null && bo != null) return ao - bo;
      if (ao != null && bo == null) return -1;
      if (ao == null && bo != null) return 1;
      return parseTs(b.created_at) - parseTs(a.created_at);
    });
    if (!manualPendingIds.length) return base;
    const byId = new Map(base.map((t) => [t.id, t]));
    const ordered = manualPendingIds.map((id) => byId.get(id)).filter(Boolean);
    const rest = base.filter((t) => !manualPendingIds.includes(t.id));
    return [...ordered, ...rest];
  }, [manualPendingIds, pending]);

  useEffect(() => {
    if (pendingSortMode !== 'manual') return;
    if (categoryFilter) return;
    const ids = pendingManualSorted.map((t) => t.id);
    setManualPendingIds(ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSortMode, categoryFilter]);

  const visiblePending = pendingSortMode === 'manual' ? pendingManualSorted : pendingCreatedSorted;

  const commitManualOrder = useCallback(async (nextIds) => {
    if (categoryFilter) return;
    try {
      await reorderTodos({ type: 'shared', ids: nextIds });
    } catch {
      loadTodos();
    }
  }, [categoryFilter, loadTodos]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
  );

  const handleManualDragEnd = useCallback(({ active, over }) => {
    if (pendingSortMode !== 'manual') return;
    if (categoryFilter) return;
    if (!over) return;
    const fromId = active?.id;
    const toId = over?.id;
    if (!fromId || !toId || fromId === toId) return;

    setManualPendingIds((prev) => {
      const base = prev.length ? prev : pendingManualSorted.map((t) => t.id);
      const fromIdx = base.indexOf(fromId);
      const toIdx = base.indexOf(toId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const next = arrayMove(base, fromIdx, toIdx);
      commitManualOrder(next);
      return next;
    });
  }, [categoryFilter, commitManualOrder, pendingManualSorted, pendingSortMode]);

  if (!authed) {
    return (
      <Page>
        <CmBackButton onClick={() => navigate(charleyMemoryConfig.routePath)} />
        <ContentArea>
          <Title>待辦事項</Title>
          <Subtitle>輸入密碼以查看和管理待辦事項</Subtitle>
          <LoginCard onSubmit={handleLogin}>
            <Label>密碼</Label>
            <Input
              type="password"
              placeholder="密碼"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              autoFocus
            />
            {authError && <ErrorBox>{authError}</ErrorBox>}
            <Btn type="submit" disabled={authLoading || !password.trim()}>
              {authLoading ? '驗證中…' : '進入'}
            </Btn>
          </LoginCard>
        </ContentArea>
      </Page>
    );
  }

  return (
    <Page>
      <CmBackButton onClick={() => navigate(charleyMemoryConfig.routePath)} />
      <ContentArea>
        <Title>待辦事項</Title>

        <ToolbarRow>
          <BigCategorySelect
            value={categoryFilter}
            onChange={(v) => setCategoryFilter(v)}
            ariaLabel="分類篩選"
            options={[
              { value: '', label: '全部分類' },
              ...allCategories.map((c) => ({ value: c, label: c })),
            ]}
          />
          <Segmented aria-label="未完成排序">
            <SegBtn type="button" $active={pendingSortMode === 'created_at'} onClick={() => setPendingSortMode('created_at')}>
              建立時間
            </SegBtn>
            <SegBtn
              type="button"
              $active={pendingSortMode === 'manual'}
              onClick={() => setPendingSortMode('manual')}
              disabled={!!categoryFilter}
              title={categoryFilter ? '手動拖拉排序請先清除分類篩選' : '手動拖拉排序'}
            >
              手動拖拉
            </SegBtn>
          </Segmented>
          {categoryFilter && pendingSortMode === 'manual' && (
            <InlineHint>手動拖拉排序請先清除分類篩選</InlineHint>
          )}
        </ToolbarRow>

        <AddRow onSubmit={handleAdd}>
          <CategoryInput
            type="text"
            list="cm-todo-categories"
            placeholder="分類（可空）"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            maxLength={80}
          />
          <AddInput
            type="text"
            placeholder="新增待辦事項..."
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            maxLength={1000}
          />
          <Btn type="submit" disabled={adding || !newText.trim()}>
            {adding ? '…' : '新增'}
          </Btn>
        </AddRow>
        <datalist id="cm-todo-categories">
          {allCategories.map((c) => <option key={c} value={c} />)}
        </datalist>

        {loading && <Subtitle>載入中…</Subtitle>}

        {visiblePending.length > 0 && (
          pendingSortMode === 'manual' && !categoryFilter ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleManualDragEnd}>
              <SortableContext items={manualPendingIds.length ? manualPendingIds : pendingManualSorted.map((t) => t.id)} strategy={verticalListSortingStrategy}>
                <TodoList>
                  {visiblePending.map((t) => (
                    <SortableTodoItem key={t.id} id={t.id} disabled={pendingSortMode !== 'manual' || !!categoryFilter}>
                      <DragHandle title="拖拉排序" aria-label="拖拉排序">☰</DragHandle>
                      <Checkbox type="button" onClick={() => handleToggle(t.id)} aria-label="標記完成">
                        &nbsp;
                      </Checkbox>

                      {editingId === t.id ? (
                        <InlineEditRow>
                          <InlineInput
                            type="text"
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            maxLength={1000}
                            autoFocus
                          />
                          <InlineEditActions>
                            <CategoryInput
                              type="text"
                              list="cm-todo-categories"
                              placeholder="分類（可空）"
                              value={editCategory}
                              onChange={(e) => setEditCategory(e.target.value)}
                              maxLength={80}
                            />
                            <Btn type="button" disabled={savingEdit || !editText.trim()} onClick={saveEdit}>
                              {savingEdit ? '…' : '保存'}
                            </Btn>
                            <SmallBtn type="button" onClick={cancelEdit}>取消</SmallBtn>
                            <InlineHint>{t.category ? `分類 ${t.category} · ` : ''}建立 {String(t.created_at || '').slice(0, 10)}</InlineHint>
                          </InlineEditActions>
                        </InlineEditRow>
                      ) : (
                        <>
                          <TodoText>{linkify(t.text)}</TodoText>
                          <TodoMeta>{t.category ? `${t.category}` : ''}</TodoMeta>
                          <SmallBtn type="button" onClick={() => beginEdit(t)} title="編輯">編輯</SmallBtn>
                        </>
                      )}

                      {t.created_by && <TodoMeta>{t.created_by}</TodoMeta>}
                      <DeleteBtn type="button" onClick={() => handleDelete(t.id)} title="刪除">✕</DeleteBtn>
                    </SortableTodoItem>
                  ))}
                </TodoList>
              </SortableContext>
            </DndContext>
          ) : (
            <TodoList>
              {visiblePending.map((t) => (
                <TodoItem key={t.id}>
                  <DragHandle style={{ opacity: 0 }} aria-hidden>☰</DragHandle>
                  <Checkbox type="button" onClick={() => handleToggle(t.id)} aria-label="標記完成">
                    &nbsp;
                  </Checkbox>

                  {editingId === t.id ? (
                    <InlineEditRow>
                      <InlineInput
                        type="text"
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        maxLength={1000}
                        autoFocus
                      />
                      <InlineEditActions>
                        <CategoryInput
                          type="text"
                          list="cm-todo-categories"
                          placeholder="分類（可空）"
                          value={editCategory}
                          onChange={(e) => setEditCategory(e.target.value)}
                          maxLength={80}
                        />
                        <Btn type="button" disabled={savingEdit || !editText.trim()} onClick={saveEdit}>
                          {savingEdit ? '…' : '保存'}
                        </Btn>
                        <SmallBtn type="button" onClick={cancelEdit}>取消</SmallBtn>
                        <InlineHint>{t.category ? `分類 ${t.category} · ` : ''}建立 {String(t.created_at || '').slice(0, 10)}</InlineHint>
                      </InlineEditActions>
                    </InlineEditRow>
                  ) : (
                    <>
                      <TodoText>{linkify(t.text)}</TodoText>
                      <TodoMeta>{t.category ? `${t.category}` : ''}</TodoMeta>
                      <SmallBtn type="button" onClick={() => beginEdit(t)} title="編輯">編輯</SmallBtn>
                    </>
                  )}

                  {t.created_by && <TodoMeta>{t.created_by}</TodoMeta>}
                  <DeleteBtn type="button" onClick={() => handleDelete(t.id)} title="刪除">✕</DeleteBtn>
                </TodoItem>
              ))}
            </TodoList>
          )
        )}

        {doneSorted.length > 0 && (
          <>
            <Subtitle>已完成 ({doneSorted.length})</Subtitle>
            <TodoList>
              {doneSorted.map((t) => (
                <TodoItem key={t.id} $done>
                  <Checkbox type="button" $checked onClick={() => handleToggle(t.id)}>✓</Checkbox>
                  <TodoText $done>{linkify(t.text)}</TodoText>
                  <TodoMeta>{t.category ? `${t.category}` : ''}</TodoMeta>
                  <DeleteBtn type="button" onClick={() => handleDelete(t.id)} title="刪除">✕</DeleteBtn>
                </TodoItem>
              ))}
            </TodoList>
          </>
        )}

        {!loading && todos.length === 0 && (
          <Subtitle>還沒有待辦事項，新增一個吧！</Subtitle>
        )}
      </ContentArea>
    </Page>
  );
};

export default CharleyMemoryTodosPage;
